// Runtime store configuration — the same build for every venue.
//
// Build-time env (.env) stays the default so per-venue APK/exe builds keep
// working unchanged. On top of it, in order:
//   1. window.__DP_CONFIG__      — injected by whoever serves the page (a hub)
//   2. the URL                   — ?app=&store=&name=&test=1&hub=&token=&device=&code=&kds=
//   3. the cached copy           — localStorage, so a cloud-served page boots offline
//   4. the cloud                 — GET {ordersApiUrl}/pos-config?appId&storeId
// Identity (app/store) only ever comes from 1–2 or the build; the cloud
// fills in name, currency, timezone and the venue's hub addresses.
// Nothing here is a secret: the DineCentral keys are public web-app keys
// and the store fields are what the receipts print.
import { config } from "./env";

export interface HubAddresses {
  lan?: string;   // 192.168.1.96
  lanTls?: string; // https://hub-lan-<venue>.dineplatform.com:8443 (what an https page must use)
  relay?: string; // https://hub-<venue>.dineplatform.com
  cloud?: string; // https://api.dineplatform.com/cloud-hub/<app>/<store>
}

export interface RuntimeSeed {
  /** Hub address from the URL (ip or url) — pre-fills Hub settings / auto-pairs with token or code. */
  hub?: string;
  token?: string;
  deviceName?: string;
  code?: string;
  /** "primary" or a station id — the page should run as a KDS. */
  kds?: string;
}

export interface RuntimeInfo {
  source: "build" | "injected" | "url" | "cache" | "cloud";
  hydratedAt: number;
  seed: RuntimeSeed;
  cloudOk: boolean;
}

const CACHE_PREFIX = "dp-runtime-config:";
// Device-local test/live override (Settings → Store → Mode). Wins over the
// build, the URL and the cloud so a venue can be tried in test mode and
// flipped live without a new build. Cleared by "Start fresh on this device".
const TEST_OVERRIDE_KEY = "dp-test-mode-override";
export function testModeOverride(): boolean | null {
  try {
    const v = localStorage.getItem(TEST_OVERRIDE_KEY);
    return v === "1" ? true : v === "0" ? false : null;
  } catch { return null; }
}
/** What the build was when the override was set. Installing a build with the
 *  OTHER mode baked in is a deliberate act by whoever installed it, and it
 *  must win — otherwise a till given a LIVE build sits in test with an empty
 *  board and nothing on screen explains why. Seen at go-live. */
const TEST_OVERRIDE_BASE = "dp-test-mode-override-base";
export function setTestModeOverride(v: boolean | null): void {
  try {
    if (v === null) {
      localStorage.removeItem(TEST_OVERRIDE_KEY);
      localStorage.removeItem(TEST_OVERRIDE_BASE);
    } else {
      localStorage.setItem(TEST_OVERRIDE_KEY, v ? "1" : "0");
      localStorage.setItem(TEST_OVERRIDE_BASE, config.testMode ? "1" : "0");
    }
  } catch { /* RAM-only: the flip lasts this session only */ }
}

/** Drops an override that a newly-installed build has overtaken. */
function dropOverrideIfBuildChanged(built: boolean): void {
  try {
    if (localStorage.getItem(TEST_OVERRIDE_KEY) === null) return;
    const base = localStorage.getItem(TEST_OVERRIDE_BASE);
    // Set by a build that recorded no baseline: adopt this one as the
    // baseline and KEEP the choice. Dropping it here would flip a till whose
    // operator deliberately switched it — the same silent wrong-mode fault,
    // in the other direction.
    if (base === null) {
      localStorage.setItem(TEST_OVERRIDE_BASE, built ? "1" : "0");
      return;
    }
    // The build's own mode has changed since the choice was made: whoever
    // installed it meant it.
    if ((base === "1") !== built) {
      localStorage.removeItem(TEST_OVERRIDE_KEY);
      localStorage.removeItem(TEST_OVERRIDE_BASE);
      console.info("[runtime] test/live override dropped — this build is " + (built ? "TEST" : "LIVE"));
    }
  } catch { /* private mode */ }
}
/** What this build/URL/cloud would run as without the device override. */
let builtTestMode: boolean | null = null;
export function testModeWithoutOverride(): boolean { return builtTestMode ?? config.testMode; }
const FETCH_TIMEOUT_MS = 4000;

function applyStoreFields(src: Record<string, unknown>, from: RuntimeInfo["source"]): boolean {
  let touched = false;
  const set = <K extends keyof typeof config>(k: K, v: unknown) => {
    if (v === undefined || v === null || v === "") return;
    (config as any)[k] = v;
    touched = true;
  };
  set("appId", typeof src.appId === "string" ? src.appId : undefined);
  set("storeId", typeof src.storeId === "string" ? src.storeId : undefined);
  set("storeName", typeof src.storeName === "string" ? src.storeName : undefined);
  set("storeAddress", typeof src.storeAddress === "string" ? src.storeAddress : undefined);
  set("storePhone", typeof src.storePhone === "string" ? src.storePhone : undefined);
  set("currencySymbol", typeof src.currencySymbol === "string" ? src.currencySymbol : undefined);
  set("currencyCode", typeof src.currencyCode === "string" ? src.currencyCode : undefined);
  set("timezone", typeof src.timezone === "string" ? src.timezone : undefined);
  // The device's own test/live choice outranks every other source, including a
  // later cloud refresh — otherwise a config poll mid-service could flip a live
  // till into test mode (an empty board, orders never reaching the kitchen)
  // with no reload and nothing on screen to explain it.
  if (typeof src.testMode === "boolean" && testModeOverride() === null) set("testMode", src.testMode);
  if (typeof src.onlineOrderingSite === "boolean") set("onlineOrderingSite", src.onlineOrderingSite);
  if (src.hubAddresses && typeof src.hubAddresses === "object") {
    config.hubAddresses = { ...(config.hubAddresses || {}), ...(src.hubAddresses as HubAddresses) };
    touched = true;
  }
  if (touched && config.runtime) config.runtime.source = from;
  return touched;
}

const LAUNCH_KEY = "dp-launch-url";
/** The URL this page was first opened with (identity + hub + kds params).
 *  Router navigation drops the query string; a reload must not lose it. */
export function launchUrl(): string | null {
  try { return localStorage.getItem(LAUNCH_KEY); } catch { return null; }
}
function rememberLaunchUrl(q: URLSearchParams) {
  if (!(q.get("app") || q.get("store"))) return;
  try { localStorage.setItem(LAUNCH_KEY, window.location.href); } catch { /* RAM-only: the launcher re-opens it */ }
}

function readUrl(): { fields: Record<string, unknown>; seed: RuntimeSeed } {
  let q = new URLSearchParams(window.location.search);
  if (q.get("app") || q.get("store")) rememberLaunchUrl(q);
  else if (!config.appId || !config.storeId) {
    // A page with no identity of its own (the cloud-served build), reloaded
    // without params (e.g. on /live): use what it was launched with. A build
    // that knows its venue (desktop, the apps) must never borrow a remembered
    // URL — the Mac launcher's POS opened as a KDS because the last remembered
    // launch carried kds=primary.
    const prev = launchUrl();
    if (prev) { try { q = new URL(prev).searchParams; } catch { /* ignore */ } }
  }
  const fields: Record<string, unknown> = {};
  const seed: RuntimeSeed = {};
  const app = q.get("app");
  const store = q.get("store");
  if (app && store) { fields.appId = app; fields.storeId = store; }
  else if (store && store.includes("/")) { const [a, s] = store.split("/"); fields.appId = a; fields.storeId = s; }
  if (q.get("name")) fields.storeName = q.get("name");
  if (q.has("test")) fields.testMode = q.get("test") !== "0" && q.get("test") !== "false";
  if (q.get("hub")) seed.hub = q.get("hub")!;
  if (q.get("token")) seed.token = q.get("token")!;
  if (q.get("device")) seed.deviceName = q.get("device")!;
  if (q.get("code")) seed.code = q.get("code")!;
  if (q.get("kds")) seed.kds = q.get("kds")!;
  return { fields, seed };
}

function cacheKey() { return `${CACHE_PREFIX}${config.appId}/${config.storeId}`; }
function readCache(): Record<string, unknown> | null {
  try { const raw = localStorage.getItem(cacheKey()); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function writeCache(doc: Record<string, unknown>) {
  try { localStorage.setItem(cacheKey(), JSON.stringify(doc)); } catch { /* RAM-only kiosks: fine */ }
}

async function fetchCloud(): Promise<Record<string, unknown> | null> {
  if (!config.appId || !config.storeId) return null;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const base = config.ordersApiUrl.replace(/\/+$/, "");
    const r = await fetch(`${base}/pos-config?appId=${encodeURIComponent(config.appId)}&storeId=${encodeURIComponent(config.storeId)}`, { signal: ctrl.signal, cache: "no-store" });
    if (!r.ok) return null;
    const d = await r.json();
    return d && typeof d === "object" ? d : null;
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

/** Re-read the store's cloud config (hub addresses move: a Pi pairs, gets a
 *  new LAN address, a relay appears). True when the cloud answered. */
export async function refreshRuntimeConfig(): Promise<boolean> {
  try {
    const cloud = await fetchCloud();
    if (!cloud) return false;
    applyStoreFields(cloud, "cloud");
    writeCache(cloud);
    if (config.runtime) config.runtime.cloudOk = true;
    return true;
  } catch {
    return false;
  }
}

/** Hydrate `config` before the app loads. Never throws; never blocks longer
 *  than the cloud fetch timeout. */
export async function hydrateRuntimeConfig(): Promise<RuntimeInfo> {
  const info: RuntimeInfo = { source: "build", hydratedAt: Date.now(), seed: {}, cloudOk: false };
  config.runtime = info;
  config.hubAddresses = config.hubAddresses || {};
  try {
    const injected = (window as any).__DP_CONFIG__;
    if (injected && typeof injected === "object") applyStoreFields(injected, "injected");
    const { fields, seed } = readUrl();
    info.seed = seed;
    applyStoreFields(fields, "url");
    const cached = readCache();
    if (cached) applyStoreFields(cached, "cache");
    const cloud = await fetchCloud();
    if (cloud) {
      applyStoreFields(cloud, "cloud");
      writeCache(cloud);
      info.cloudOk = true;
    }
    // A URL-supplied hub wins over anything the cloud knows.
    if (seed.hub) config.hubAddresses = { ...config.hubAddresses, lan: seed.hub };
    builtTestMode = config.testMode;
    dropOverrideIfBuildChanged(config.testMode);
    const override = testModeOverride();
    if (override !== null) config.testMode = override;
  } catch (e) {
    console.warn("[runtime-config]", e);
  }
  return info;
}

export function runtimeSeed(): RuntimeSeed {
  return config.runtime?.seed || {};
}

// Which entry is running. Kept here (not in App) so Layout/screens can ask
// without importing App (cycle). "kds" = kds.html, or ?kds=… on the POS page.
export type AppVariant = "pos" | "kds";
let appVariant: AppVariant = "pos";
export function setAppVariant(v: AppVariant): void { appVariant = v; }
export function currentAppVariant(): AppVariant { return appVariant; }
/** This page is a kitchen display: behave as KDS mode whatever the saved setting says. */
export function isKdsPage(): boolean { return appVariant === "kds" || !!runtimeSeed().kds; }
